import type {
  CommonMultiPeriodMeta,
  ParamsBasePeriodik,
  PeriodikPegawaiItem,
  PeriodikItem,
  PeriodikType,
} from 'types/kinerja'
import type { TAtasanStatus } from 'types/kinerja-atasan'
import type {
  TPenetapanRatingPerilakuKerja,
  TPenetapanRatingPraktikKinerja,
} from 'types/kinerja-ks-pelaksanaan'
import type { CommonListResponse, CommonResponse } from 'types/api'
import type {
  TPerformancePracticeResult,
  TWorkBehaviorResult,
  TPredicateRecommendationResult,
  RecommendationResultV2,
} from 'types/kinerja-pelaksanaan'

// Objects
export enum DeliveryStatus {
  ALL = 'all',
  SUDAH_DIKIRIM = 'sudahDikirim',
  BELUM_DIKIRIM = 'belumDikirim',
  BELUM_DINILAI = 'belumDinilai',
}

export enum Predicates {
  nilaiSangatBaik = 'nilaiSangatBaik',
  nilaiBaik = 'nilaiBaik',
  nilaiButuhPerbaikan = 'nilaiButuhPerbaikan',
  nilaiKurang = 'nilaiKurang',
  nilaiSangatKurang = 'nilaiSangatKurang',
}

export type TPredicateKey = keyof typeof Predicates
export type TPredicates = Record<TPredicateKey, number>

export type TPenilaian = {
  id: string
  name: string
  jenjang: string
  jabatan?: string
  satuanPendidikan: string
  npsn: string
  predikatKinerja: string
  estimasiAngkaKredit: number
  statePengiriman: 'SUDAH_DIKIRIM' | 'BELUM_DIKIRIM'
  ratingPraktikKinerja: TPenetapanRatingPraktikKinerja
  ratingPerilakuKerja: TPenetapanRatingPerilakuKerja
  poinPengembanganKompetensi: number
  confirmedSubordinate: TAtasanStatus
}

export type TPenilaianTujuanState = 'PS_TO_DINAS' | 'PS_TO_KS' | 'DINAS_TO_KS'

export type TPenilaianMeta = {
  year: number
  counts: {
    all: number
    belumDikirim: number
    sudahDikirim: number
    belumDinilai: number
  }
}

export type TPenilaianFilterStatus = keyof typeof DeliveryStatus

// API Payload
export type TPayloadPenilaianForDelivery = {
  id: TPenilaian['id'][]
  state: TPenilaianTujuanState
}

export type TPayloadPenilaian = {
  id: TPenilaian['id']
  ratingPraktikKinerja: TPenetapanRatingPraktikKinerja
  ratingPerilakuKerja: TPenetapanRatingPerilakuKerja
}

// API Responses
export type TResponsePenilaian = CommonListResponse<
  TPenilaian,
  CommonMultiPeriodMeta & TPenilaianMeta
>

export type TResponsePenilaianSummaryData = Record<TPredicateKey, number> & {
  belumDikirim: number
}

export type TResponsePenilaianSummary = {
  data: TResponsePenilaianSummaryData
}

export type TResponsePenilaianForDelivery = {
  totalSent: number
}

export type TResponsePenilaianCount = CommonResponse<{
  all: number
  belumDikirim: number
  sudahDikirim: number
  belumDinilai: number
}>

export type TResponsePredikatKinerja = CommonListResponse<Partial<TPenilaian>>

export interface TParamsApproveBuktiKinerja {
  npsn: string
}

export type ParamsGetKsPeriodikList = ParamsBasePeriodik
export type ResponseGetKsPeriodikList = CommonResponse<{
  periodicType: PeriodikType
  periods: Array<PeriodikItem>
}>

export type ResponseGetKsPeriodikType = CommonResponse<{
  periodicType: PeriodikType
}>

export type ParamsGetKsPeriodikDetail = ParamsBasePeriodik & {
  skpMonth: number
}
export type ResponseGetKsPeriodikDetail = CommonListResponse<
  PeriodikPegawaiItem,
  { periodicLabel: string; endingDate: string }
>

export type ParamsGetKsPeriodikPegawaiDetail = ParamsBasePeriodik & {
  skpMonth: number
  ptkId: string
}

export type ResponseGetKsPeriodikPegawaiDetail = CommonResponse<
  PeriodikPegawaiItem,
  { periodicLabel: string; endingDate: string }
>

export type ParamsBulkKsPenilaianPeriodikPegawai = Pick<
  PeriodikPegawaiItem,
  'performancePractice' | 'workBehavior' | 'realization' | 'feedback'
> & {
  ptkIds: Array<string>
  skpYear: number
  skpMonth: number
}

export type ParamsBulkKsSendPeriodikToBkn = {
  ptkIds: Array<string>
  skpMonth: number
  skpYear: number
}

export type ResponseGetPenilaianKsByPsV2 = CommonListResponse<
  PenilaianItem,
  CommonMultiPeriodMeta & TPenilaianMeta
>

export type PenilaianItem = {
  id: string
  name: string
  jenjang: string
  jabatan: string
  satuanPendidikan: string
  npsn: string
  predikatKinerja: '' | TPredicateRecommendationResult
  statePengiriman: StatusPengirimanType
  ratingPraktikKinerja: '' | TPerformancePracticeResult
  ratingPerilakuKerja: '' | TWorkBehaviorResult
  nip: string
  confirmedSubordinate: TAtasanStatus
}

export type StatusPengirimanType =
  | 'SUDAH_DIKIRIM'
  | 'BELUM_DIKIRIM'
  | 'BELUM_DINILAI'

export type ParamsBulkOverridePenilaianKsByPs = Array<{
  id: string
  ratingPraktikKinerja: TPerformancePracticeResult
  ratingPerilakuKerja: TWorkBehaviorResult
}>

export type ResponseGetPenilaianGuruByPsV2 = CommonListResponse<
  PenilaianGuruItem,
  CommonMultiPeriodMeta & TPenilaianMeta
>

export type PenilaianGuruItem = Pick<
  PenilaianItem,
  | 'name'
  | 'jabatan'
  | 'satuanPendidikan'
  | 'npsn'
  | 'predikatKinerja'
  | 'statePengiriman'
  | 'ratingPraktikKinerja'
  | 'ratingPerilakuKerja'
  | 'confirmedSubordinate'
> & { ptkId: string }

export type ParamsBulkOverridePenilaianGuruByPs = Array<{
  ptkId: string
  ratingPraktikKinerja: TPerformancePracticeResult
  ratingPerilakuKerja: TWorkBehaviorResult
}>

export type GetDetailPenilaianKSByPSV2 = {
  id: string
  name: string
  satuanPendidikan: string
  npsn: string
  predikatKinerja: RecommendationResultV2<TPredicateRecommendationResult>
  ratingPraktikKinerja: RecommendationResultV2<TPerformancePracticeResult>
  ratingPerilakuKerja: RecommendationResultV2<TWorkBehaviorResult>
  statePengiriman: StatusPengirimanType
  jabatan: string
}

export type ResponseGetDetailPenilaianKSByPSV2 =
  CommonResponse<GetDetailPenilaianKSByPSV2>

export type GetDetailPenilaianGuruByPSV2 = {
  ptkId: string
  name: string
  satuanPendidikan: string
  npsn: string
  predikatKinerja: RecommendationResultV2<TPredicateRecommendationResult>
  ratingPraktikKinerja: RecommendationResultV2<TPerformancePracticeResult>
  ratingPerilakuKerja: RecommendationResultV2<TWorkBehaviorResult>
  statePengiriman: StatusPengirimanType
  jabatan: string
}

export type ResponseGetDetailPenilaianGuruByPSV2 =
  CommonResponse<GetDetailPenilaianGuruByPSV2>
